import {
  addLineModeAtom,
  addPointModeAtom,
  addPolygonModeAtom,
  addSurfaceModeAtom,
  elevationProfileToolModeAtom,
  meshPickTargetsAtom,
  pickedPointAtom,
  pointCloudTargetsAtom,
  viewerEvents,
} from '@/atoms';
import { pipeToolModeAtom } from '@/atoms/pipe.atom';
import { stockpileToolModeAtom } from '@/atoms/stockpile.atom';
import { useThree } from '@react-three/fiber';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import { getFragments } from '../IFC2';

const DRAG_THRESHOLD = 5;

type ScenePick = {
  point: THREE.Vector3;
  source: 'pointcloud' | 'mesh' | 'fragment';
  attributes?: Record<string, number | number[]>;
  layerId?: string;
};

/**
 * Unified right-click picker for the 3D scene.
 * Raycasts against both point clouds (via Potree pick) and mesh targets
 * (IFC, LandXML, Fragments, etc.) and writes the closest hit to pickedPointAtom
 * so the shared ViewerContextMenu appears.
 *
 * Also handles double-click: a double-click whose closest hit is a point-cloud
 * point re-centers the orbit target there (same `setCenterTarget` event as the
 * context menu's "Set as center"). It deliberately stays out of the way when
 * anything else owns the click: GeoJSON features (single-click select), LandXML
 * / IFC fragment models (double-click select), or an active drawing / measuring
 * tool.
 */
export const SceneContextMenuPicker = () => {
  const { camera, gl, scene } = useThree();
  const meshTargets = useAtomValue(meshPickTargetsAtom);
  const pointCloudTargets = useAtomValue(pointCloudTargetsAtom);
  const setPickedPoint = useSetAtom(pickedPointAtom);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);

  // Any tool that consumes left clicks (vertex picking) or dblclick
  // (finalize) suppresses double-click-to-center entirely.
  const addPointMode = useAtomValue(addPointModeAtom);
  const addSurfaceMode = useAtomValue(addSurfaceModeAtom);
  const addLineMode = useAtomValue(addLineModeAtom);
  const addPolygonMode = useAtomValue(addPolygonModeAtom);
  const elevationProfileMode = useAtomValue(elevationProfileToolModeAtom);
  const stockpileMode = useAtomValue(stockpileToolModeAtom);
  const pipeMode = useAtomValue(pipeToolModeAtom);
  const [params] = useSearchParams();
  const toolActive =
    addPointMode.enabled ||
    addSurfaceMode.enabled ||
    addLineMode.enabled ||
    addPolygonMode.enabled ||
    elevationProfileMode.phase === 'drawing' ||
    stockpileMode.phase === 'picking' ||
    pipeMode.phase === 'seeking' ||
    params.get('showMeasurements') === 'true' ||
    params.get('showAreaMeasurement') === 'true';

  useEffect(() => {
    const canvas = gl.domElement;
    let mouseDownPos: { x: number; y: number } | null = null;

    // Shared closest-hit picker for both the context menu and
    // double-click-to-center. Sets `raycaster` from the event, so callers can
    // reuse `raycaster.ray` afterwards.
    const pickScene = async (e: MouseEvent): Promise<ScenePick | null> => {
      const rect = canvas.getBoundingClientRect();
      const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);

      // --- Point cloud picking ---
      let pcHit: THREE.Vector3 | null = null;
      let pcAttributes: Record<string, number | number[]> | undefined;
      let pcLayerId: string | undefined;
      if (pointCloudTargets.length > 0) {
        const hits = pointCloudTargets
          .map((pco) => pco.pick(gl, camera, raycaster.ray))
          .filter(Boolean);
        if (hits.length > 0 && hits[0]?.position) {
          const hit = hits[0]!;
          pcHit = hit.position!.clone();
          // Copy non-position/normal numeric attributes (e.g. Deviation,
          // intensity, classification) into a plain dict so the context
          // menu can render them. The picker writes itemSize-1 attribs as
          // bare numbers and N-vector attribs as number[].
          const attrs: Record<string, number | number[]> = {};
          for (const k of Object.keys(hit)) {
            if (k === 'position' || k === 'normal' || k === 'pointCloud') continue;
            const v = (hit as Record<string, unknown>)[k];
            if (typeof v === 'number' || Array.isArray(v)) {
              attrs[k] = v as number | number[];
            }
          }
          pcAttributes = Object.keys(attrs).length ? attrs : undefined;
          const ud = (hit.pointCloud as THREE.Object3D | undefined)?.userData;
          pcLayerId = (ud?.layerId as string | undefined) ?? undefined;
        }
      }

      // --- Mesh picking ---
      let meshHit: THREE.Vector3 | null = null;
      if (meshTargets.length > 0) {
        try {
          const prevLineThreshold = raycaster.params.Line.threshold;
          raycaster.params.Line.threshold = 0.1;
          const intersections = raycaster.intersectObjects(meshTargets, true);
          raycaster.params.Line.threshold = prevLineThreshold;
          if (intersections.length > 0) {
            meshHit = intersections[0].point.clone();
          }
        } catch {
          // Fragment LOD meshes may throw on raycast — skip silently
        }
      }

      // --- Fragment model picking (ThatOpen native raycast) ---
      let fragmentHit: THREE.Vector3 | null = null;
      try {
        const fragments = await getFragments();
        if (fragments && fragments.models.list.size > 0) {
          const mouse = new THREE.Vector2(e.clientX, e.clientY);
          for (const [, model] of fragments.models.list) {
            try {
              const result = await model.raycast({
                camera: camera as THREE.PerspectiveCamera,
                mouse,
                dom: canvas,
              });
              if (result) {
                const d = raycaster.ray.origin.distanceTo(result.point);
                if (!fragmentHit || d < raycaster.ray.origin.distanceTo(fragmentHit)) {
                  fragmentHit = result.point.clone();
                }
              }
            } catch {
              // model may not support raycasting
            }
          }
        }
      } catch {
        // fragments not initialized
      }

      // Pick the closest hit to the camera
      const origin = raycaster.ray.origin;
      let best: ScenePick | null = null;
      let bestDist = Infinity;

      if (pcHit) {
        const d = origin.distanceTo(pcHit);
        if (d < bestDist) {
          bestDist = d;
          best = { point: pcHit, source: 'pointcloud', attributes: pcAttributes, layerId: pcLayerId };
        }
      }
      if (meshHit) {
        const d = origin.distanceTo(meshHit);
        if (d < bestDist) {
          bestDist = d;
          best = { point: meshHit, source: 'mesh' };
        }
      }
      if (fragmentHit) {
        const d = origin.distanceTo(fragmentHit);
        if (d < bestDist) {
          bestDist = d;
          best = { point: fragmentHit, source: 'fragment' };
        }
      }

      return best;
    };

    // Distance to the nearest GeoJSON feature object under the cursor, or
    // null. Features live outside meshPickTargetsAtom (they use R3F onClick
    // for selection), so we raycast scene objects tagged with
    // userData.featureId. Uses the same Line threshold as R3F's default
    // raycaster so "would this click select a feature?" matches reality.
    const nearestFeatureDistance = (): number | null => {
      const featureObjects: THREE.Object3D[] = [];
      scene.traverse((o) => {
        if (o.visible && o.userData?.featureId && (o as THREE.Mesh).geometry) {
          featureObjects.push(o);
        }
      });
      if (featureObjects.length === 0) return null;
      const prevLineThreshold = raycaster.params.Line.threshold;
      raycaster.params.Line.threshold = 1;
      let hits: THREE.Intersection[] = [];
      try {
        hits = raycaster.intersectObjects(featureObjects, false);
      } catch {
        // malformed feature geometry — treat as no hit
      }
      raycaster.params.Line.threshold = prevLineThreshold;
      return hits.length > 0 ? hits[0].distance : null;
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 2) {
        mouseDownPos = { x: e.clientX, y: e.clientY };
      }
    };

    const handleMouseUp = async (e: MouseEvent) => {
      if (e.button !== 2 || !mouseDownPos) return;

      const dx = e.clientX - mouseDownPos.x;
      const dy = e.clientY - mouseDownPos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      mouseDownPos = null;

      if (dist >= DRAG_THRESHOLD) return;

      const pick = await pickScene(e);
      if (!pick) return;

      const cameraDirection = new THREE.Vector3();
      camera.getWorldDirection(cameraDirection);
      const cameraAngle = (Math.atan2(cameraDirection.x, cameraDirection.y) * 180) / Math.PI;

      setPickedPoint({
        position: pick.point,
        screenX: e.clientX,
        screenY: e.clientY,
        cameraAngle,
        // Only attach point-cloud attributes when the chosen hit was the
        // point-cloud (not a mesh): keeps the tooltip honest about what
        // the values describe.
        attributes: pick.source === 'pointcloud' ? pick.attributes : undefined,
        pointCloudLayerId: pick.source === 'pointcloud' ? pick.layerId : undefined,
      });
    };

    const handleDoubleClick = async (e: MouseEvent) => {
      if (e.button !== 0 || toolActive) return;

      const pick = await pickScene(e);
      // Only center on point-cloud hits. When a mesh or fragment model is the
      // closest hit, its own double-click selection (LandXML onDoubleClick,
      // IFC2 dblclick raycast) owns the gesture.
      if (!pick || pick.source !== 'pointcloud') return;

      // A GeoJSON feature in front of the picked point gets selected by the
      // R3F click handlers — don't fight it by also moving the camera.
      const featureDist = nearestFeatureDistance();
      if (featureDist !== null && featureDist < raycaster.ray.origin.distanceTo(pick.point)) return;

      viewerEvents.emit('setCenterTarget', {
        x: pick.point.x,
        y: pick.point.y,
        z: pick.point.z,
      });
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('dblclick', handleDoubleClick);
    canvas.addEventListener('contextmenu', handleContextMenu);
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('dblclick', handleDoubleClick);
      canvas.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [camera, gl, scene, meshTargets, pointCloudTargets, raycaster, setPickedPoint, toolActive]);

  return null;
};
